import type { IconName } from '@customerio/pluma-icons';

// Ember `TYPE_ICONS` from `cio/models/campaign`. The wire enum has a couple
// of values the Ember map never had (`transactional_message`, `v3`) — those
// fall back to the generic campaigns icon instead of a broken glyph.
const CAMPAIGN_TYPE_ICONS: Record<string, IconName> = {
  behavioral: 'segment',
  seg_attr: 'segment',
  rel_seg_attr: 'object',
  transactional: 'trigger-event',
  triggered_broadcast: 'api-triggered-broadcast',
  date: 'trigger-date',
  form: 'form',
  webhook: 'webhook',
  object: 'object',
  relationship: 'connections',
  geofence: 'geofences',
  none: 'campaigns',
};

export function campaignTypeIcon(type: string): IconName {
  return CAMPAIGN_TYPE_ICONS[type] ?? 'campaigns';
}

export interface CampaignTypeNoun {
  singular: string;
  titleSingular: string;
}

/** Ember `campaign.typeName` — human name for a campaign trigger type. */
export function campaignTypeName(type: string, campaignsNoun: CampaignTypeNoun): string {
  const { singular, titleSingular } = campaignsNoun;
  const names: Record<string, string> = {
    behavioral: `Segment triggered ${singular}`,
    seg_attr: `Attribute or segment triggered ${singular}`,
    rel_seg_attr: `Object triggered ${singular}`,
    transactional: `Event triggered ${singular}`,
    form: `Form triggered ${singular}`,
    date: `Date triggered ${singular}`,
    triggered_broadcast: 'API-triggered broadcast',
    webhook: `Webhook triggered ${singular}`,
    object: `Object triggered ${singular}`,
    relationship: `Relationship triggered ${singular}`,
    geofence: `Geofence triggered ${singular}`,
    none: titleSingular,
  };
  return names[type] ?? titleSingular;
}

/** The wire type of an API-triggered broadcast. */
const API_TRIGGERED_BROADCAST_TYPE = 'triggered_broadcast';

/** Whether a campaign is an API-triggered broadcast rather than an automation. */
export function isApiTriggeredBroadcast(campaign: { type?: string | null }): boolean {
  return campaign.type === API_TRIGGERED_BROADCAST_TYPE;
}

/** Whether a campaign is triggered by a webhook, which may have no person behind it. */
export function isWebhookTriggered(campaign: { type?: string | null }): boolean {
  return campaign.type === 'webhook';
}

/** Whether a campaign is triggered by an event. Its wire type is `transactional`. */
export function isEventTriggered(campaign: { type?: string | null }): boolean {
  return campaign.type === 'transactional';
}

/** The permission a viewer needs to edit a campaign. */
export type CampaignEditPermission = 'journeys/broadcasts/edit' | 'journeys/campaigns/edit';

/** The permission a viewer needs to run a campaign, and to change one that is live. */
export type CampaignExecutePermission = 'journeys/broadcasts/execute' | 'journeys/campaigns/execute';

/**
 * Which permission gates editing a campaign of this wire type. An
 * API-triggered broadcast has its own permission, because an account can
 * grant one and not the other.
 */
export function campaignEditPermission(campaignType: string | null | undefined): CampaignEditPermission {
  return campaignType === API_TRIGGERED_BROADCAST_TYPE ? 'journeys/broadcasts/edit' : 'journeys/campaigns/edit';
}

/** Which permission gates running a campaign of this wire type, or changing it while it is live. */
export function campaignExecutePermission(campaignType: string | null | undefined): CampaignExecutePermission {
  return campaignType === API_TRIGGERED_BROADCAST_TYPE ? 'journeys/broadcasts/execute' : 'journeys/campaigns/execute';
}
